/**
 * The auth surface, one function per route in `apps/handbook-mcp/app/api/v1/auth.py`.
 *
 * `register` deliberately returns an account and no session — the endpoint's own docstring says
 * "Returns no session — log in next", so the caller logs in as a second call.
 */

import { request } from "./client";
import type { Account, AccountDeleted, Session } from "./types";

export function register(email: string, password: string): Promise<Account> {
  return request<Account>("/auth/register", {
    method: "POST",
    body: { email, password },
  });
}

export function login(email: string, password: string): Promise<Session> {
  return request<Session>("/auth/login", {
    method: "POST",
    body: { email, password },
  });
}

/** Rotates. The presented token is revoked whether or not the new one is ever used. */
export function refresh(refreshToken: string): Promise<Session> {
  return request<Session>("/auth/refresh", {
    method: "POST",
    body: { refresh_token: refreshToken },
  });
}

/** 204 even for an unknown token, by design — so this never fails in a way worth showing. */
export function logout(refreshToken: string): Promise<void> {
  return request<void>("/auth/logout", {
    method: "POST",
    body: { refresh_token: refreshToken },
  });
}

export function me(accessToken: string): Promise<Account> {
  return request<Account>("/auth/me", { accessToken });
}

/**
 * Irreversible. The API requires the account's own address typed back and an explicit
 * acknowledgement, so this cannot be reached by a mis-scripted client.
 */
export function deleteAccount(accessToken: string, confirmEmail: string): Promise<AccountDeleted> {
  return request<AccountDeleted>("/account", {
    method: "DELETE",
    accessToken,
    body: { confirm_email: confirmEmail, acknowledge_irreversible: true },
  });
}
